import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import { useContext, useEffect, useState } from "react";
import styles from "./syles.module.css";
import {
  buscarVersoesdaPrevia,
  buscarVersaoCompletaPrevia,
  criarVersaoPrevia,
  buscarVersoesProjeto,
  buscarVersaoCompletaProjeto,
  criarVersaoProjeto,
  iniciarProjetoInss,
} from "../../../services/api-inss.service";
import { receberCredito } from "../../../services/api-conta-corrente.service";
import { listarEmpresas } from "../../../services/api-empresas.service";
import SnackBar from "../../../components/SnackBar";
import Loading from "../../../components/Loading";
import { PreviaInssContext } from "../PreviaInssContext";
import ModalAnalisar from "../ModalAnalisar";
import ModalObservacoesInss from "../ModalAnalisar/ModalObservacoesInss";
import {
  preferLiveFolhaOverSnapshot,
  sanitizeVersaoSnapshotArrays,
} from "../../../utils/previaInssFolhaCoherence";

const ModalVersoes = ({ open, onClose, id, tipo, data, rechargeData, gerarProjeto, projeto }) => {
  const { dados, setDados, buscaDadosPrevia, setLoadingVersao } = useContext(PreviaInssContext);
  const [loading, setLoading] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [modalNovaVersao, setModalNovaVersao] = useState(false);
  const [openOrCloseModalAnalisar, setOpenOrCloseModalAnalisar] = useState(false);
  const getIdVersao = (v) => {
    if (!v) return 0;
    return (
      v.idPreviaVersao ??
      v.idProjetoVersao ??
      v.idVersao ??
      v.id ??
      0
    );
  };
  const [versoes, setVersoes] = useState([
    {
      idPreviaVersao: 0,
      descricao: "Original",
      dtCadastro: null,
      versao: null,
    },
  ]);
  const [novaVersao, setNovaVersao] = useState({ copiar: "", nome: "" });
  const [openObs, setOpenObs] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = projeto
          ? await buscarVersoesProjeto(tipo, id)
          : await buscarVersoesdaPrevia(tipo, id);

        // Normaliza diferentes formatos de retorno da API para sempre obter arrays válidos
        const isArr = Array.isArray;
        let apiVersoes = [];
        let apiProjetos = [];

        if (isArr(response?.versoes)) apiVersoes = response.versoes;
        else if (isArr(response?.detalhes?.versoes)) apiVersoes = response.detalhes.versoes;
        else if (isArr(response?.detalhes)) apiVersoes = response.detalhes; // às vezes vem direto
        else if (isArr(response?.data)) apiVersoes = response.data;
        else if (isArr(response)) apiVersoes = response;

        if (isArr(response?.projetos)) apiProjetos = response.projetos;
        else if (isArr(response?.detalhes?.projetos)) apiProjetos = response.detalhes.projetos;

        // Garante campos mínimos para renderização (descricao/dtCadastro) e ID acessível
        // IMPORTANTE: Não carrega os dados completos da versão (campo 'versao') aqui
        // Apenas metadados para exibição na lista
        const withSafeFields = (list = []) =>
          list.filter(Boolean).map((v, idx) => ({
            idPreviaVersao: v.idPreviaVersao || v.idProjetoVersao || v.idVersao || v.id || idx + 1,
            descricao: v.descricao || v.nome || `Versão ${idx + 1}`,
            dtCadastro: v.dtCadastro || v.dataCadastro || v.createdAt || v.dtCriacao || null,
            // Não incluir o campo 'versao' aqui - será carregado apenas quando necessário
            // Mantém outros campos que possam ser úteis para identificação
            idProjeto: v.idProjeto || null,
            idPrevia: v.idPrevia || null,
          }));

        apiVersoes = withSafeFields(apiVersoes);
        apiProjetos = withSafeFields(apiProjetos);

        // Não carrega dados completos aqui - apenas quando necessário (ao clicar em Analisar)
        // await buscaDadosPrevia(data.idEmpresa); // Removido - será carregado apenas quando necessário

        setVersoes([
          {
            idPreviaVersao: 0,
            descricao: "Original",
            dtCadastro: null,
            versao: null,
          },
          // insere sempre as versões (apenas metadados)
          ...apiVersoes,
          // só insere os projetos quando estivermos no modo "projeto"
          ...(projeto ? apiProjetos : []),
        ]);

      } catch (error) {
        console.error("Erro ao buscar versões:", error);
        setObjSnackBar({ type: "error", msg: "Erro ao buscar versões." });
      } finally { setLoading(false) }
    };

    if (id && open) {
      fetchData();
    }
  }, [id, projeto, open]);

  const handleClose = () => {
    setDados((prev) => ({
      ...prev,
    }));
    setModalNovaVersao(false);
    setVersoes([{ idPreviaVersao: 0, descricao: "Original" }]);
    setNovaVersao({ copiar: "", nome: "" });
    onClose();
  };

  const handleChangeCopia = (event) => {
    const selected = event.target.value;
    // generate a suggested name following the owner spec: "Tipo |Nome cliente | Data | Id"
    try {
      const generateSuggestedName = (versaoObj) => {
        const tipoLabel = projeto ? 'Projeto' : 'Prévia';
        // compute next sequential counter (exclude the 'Original' with id 0)
        const existingCount = Array.isArray(versoes) ? versoes.filter(v => getIdVersao(v) !== 0).length : 0;
        const counter = existingCount + 1;
        // Try several common places for company/razao social
        // Nota: versaoObj não tem mais o campo 'versao' na listagem inicial
        const companyCandidates = [
          (dados && (dados.razaoSocial || dados.nome || dados.clienteNome)),
          (data && (data.razaoSocial || data.nome || data.clienteNome)),
        ];
        let comp = companyCandidates.find((c) => c && String(c).trim()) || '';
        comp = String(comp).trim().split(/\s+/)[0] || '';
        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const yyyy = now.getFullYear();
        const dateStr = `${dd}/${mm}/${yyyy}`;
        return `${tipoLabel} ${comp} ${dateStr} ${counter}`.trim();
      };

      const suggested = generateSuggestedName(selected);
      setNovaVersao({ ...novaVersao, copiar: selected, nome: suggested });
    } catch (e) {
      setNovaVersao({ ...novaVersao, copiar: selected });
    }
  };

  const handleCreate = async () => {
    setLoadingCreate(true);
    try {
      // 1) define qual objeto vamos copiar
      let versaoParaCopiar = novaVersao.copiar;
      let versaoData = null;
      
      if (versaoParaCopiar.idPreviaVersao === 0 || versaoParaCopiar.idProjetoVersao === 0) {
        // Versão original - usa dados atuais
        versaoData = dados;
      } else {
        // Versão específica - busca dados completos da versão
        const idVersaoSel = getIdVersao(versaoParaCopiar);
        try {
          if (projeto) {
            const response = await buscarVersaoCompletaProjeto(idVersaoSel);
            versaoData = response?.versao || response?.data?.versao || response;
          } else {
            const response = await buscarVersaoCompletaPrevia(idVersaoSel);
            versaoData = response?.versao || response?.data?.versao || response;
          }
        } catch (error) {
          console.error("Erro ao buscar dados da versão para copiar:", error);
          setObjSnackBar({
            type: "error",
            msg: "Erro ao buscar dados da versão selecionada.",
          });
          setLoadingCreate(false);
          return;
        }
      }

      if (!versaoData) {
        setObjSnackBar({
          type: "error",
          msg: "Não foi possível obter os dados da versão para copiar.",
        });
        setLoadingCreate(false);
        return;
      }

      const cnpjLimpo = String(data?.cnpj || "").replace(/\D/g, "");
      const versaoSanitizada =
        cnpjLimpo.length === 14
          ? sanitizeVersaoSnapshotArrays(versaoData, cnpjLimpo)
          : versaoData;

      // 2) monta o payload comum
      const body = {
        descricao: novaVersao.nome,
        tipoPrevia: tipo,
        idPrevia: id,
        versao: versaoSanitizada,
      };

      if (projeto) {
        // (b) injeta o ID do projeto no payload
        body.idProjeto = versaoParaCopiar.idProjeto;
        // (c) chama o endpoint de versão de projeto
        await criarVersaoProjeto(body);
        setObjSnackBar({ type: "success", msg: "Projeto gerado com sucesso!" });
      } else {
        // --- criar versão padrão de prévia ---
        await criarVersaoPrevia(body);
        setObjSnackBar({ type: "success", msg: "Versão criada com sucesso!" });
      }

      // 3) fecha modal e recarrega
      handleClose();
      rechargeData();
    } catch (error) {
      console.error("Erro ao criar:", error);
      setObjSnackBar({
        type: "error",
        msg: error.response?.data?.mensagem || "Ops! Erro ao criar.",
      });
    } finally {
      setLoadingCreate(false);
    }
  };


  const handleGerarProjeto = async (versao) => {
    setLoadingCreate(true);
    try {
      const resp = await iniciarProjetoInss(id);                 // id == idPrevia (prop do modal)
      const idNovoProjeto = Number(resp.idProjetoInss);
      const codigoProjeto = `INSS-${idNovoProjeto}`;

      // monta a VERSÃO ORIGINAL do PROJETO a partir da versão escolhida da PRÉVIA
      let versaoData = null;
      const idVersaoSel = getIdVersao(versao);
      
      if (idVersaoSel === 0) {
        // Versão original - usa dados atuais
        versaoData = dados;
      } else {
        // Versão específica - busca dados completos da versão
        try {
          const response = await buscarVersaoCompletaPrevia(idVersaoSel);
          versaoData = response?.versao || response?.data?.versao || response;
        } catch (error) {
          console.error("Erro ao buscar dados da versão para gerar projeto:", error);
          setObjSnackBar({
            type: "error",
            msg: "Erro ao buscar dados da versão selecionada.",
          });
          setLoadingCreate(false);
          return;
        }
      }

      if (!versaoData) {
        setObjSnackBar({
          type: "error",
          msg: "Não foi possível obter os dados da versão para gerar projeto.",
        });
        setLoadingCreate(false);
        return;
      }

      const cnpjProj = String(data?.cnpj || "").replace(/\D/g, "");
      const versaoProjetoLimpa =
        cnpjProj.length === 14
          ? sanitizeVersaoSnapshotArrays(versaoData, cnpjProj)
          : versaoData;

      const body = {
        descricao: "Original",            // <- vira a versão "original" do projeto
        idProjeto: idNovoProjeto,
        tipoPrevia: tipo,
        idPrevia: id,                     // referência da prévia origem
        versao: versaoProjetoLimpa,               // snapshot da versão escolhida
      };

      await criarVersaoProjeto(body);
      
      // Criar/atualizar crédito na conta corrente com código do projeto
      const cnpj = data?.cnpj;
      if (cnpj) {
        try {
          const cnpjLimpo = String(cnpj).replace(/\D/g, '');
          
          // Calcular valor total da versão escolhida (sanitizada)
          const valorTotal = Array.isArray(versaoProjetoLimpa)
            ? versaoProjetoLimpa.reduce((acc, item) => acc + (item.valor || 0), 0)
            : (versaoProjetoLimpa?.valorTotal || versaoProjetoLimpa?.valor || 0);

          let valorFinal = valorTotal;
          if (valorFinal === 0 && versaoProjetoLimpa?.resumo) {
            valorFinal = Array.isArray(versaoProjetoLimpa.resumo)
              ? versaoProjetoLimpa.resumo.reduce((acc, item) => acc + (item.valor || 0), 0)
              : 0;
          }
          
          if (valorFinal > 0) {
            // Usar idPrevia no external_event_id para garantir idempotência
            // Se já existir crédito da aprovação da prévia, este será um crédito adicional com código do projeto
            const externalEventId = `projeto-inss-${id}-${idNovoProjeto}`;
            
            await receberCredito({
              cnpj: cnpjLimpo,
              projeto_codigo: codigoProjeto,
              tipo_credito: "INSS",
              valor: valorFinal,
              external_event_id: externalEventId,
              sistema_origem: "app-planejamento-tributario",
            });

            console.log("✅ Crédito criado/atualizado na conta corrente com código do projeto");
          }
        } catch (creditoError) {
          console.error("⚠️ Erro ao criar crédito na conta corrente:", creditoError);
          // Não falhar a geração do projeto se houver erro ao criar crédito
        }
      }
      
      setObjSnackBar({ type: "success", msg: "Projeto gerado com sucesso!" });
      handleClose();
      rechargeData();
    } catch (error) {
      console.error("Erro ao gerar projeto:", error);
      setObjSnackBar({ type: "error", msg: error.response?.data?.mensagem || "Erro ao gerar projeto." });
    } finally {
      setLoadingCreate(false);
    }
  };

const handleAnalisar = async (versao) => {
  const idVersaoSel = getIdVersao(versao);

  const cnpjSelecionado = String(data?.cnpj || "").replace(/\D/g, "");
  if (cnpjSelecionado) {
    sessionStorage.setItem("cnpjSelecionado", cnpjSelecionado);
  }

  // Id da empresa para buscar verbas, impostos, relatório 20s, bases, resumo
  let idParaBusca = data?.idEmpresa ?? data?.id_empresa ?? data?.idEmpresaCliente ?? null;
  if (!idParaBusca && cnpjSelecionado) {
    try {
      const resEmpresas = await listarEmpresas({ cnpj: cnpjSelecionado });
      const empresas = Array.isArray(resEmpresas?.data)
        ? resEmpresas.data
        : Array.isArray(resEmpresas)
        ? resEmpresas
        : [];
      const empresa = empresas[0];
      idParaBusca =
        empresa?.idEmpresa ??
        empresa?.id_empresa ??
        empresa?.id ??
        null;
    } catch (e) {
      console.warn("Não foi possível resolver idEmpresa pelo CNPJ selecionado.", e);
    }
  }
  if (!idParaBusca) {
    setObjSnackBar({ type: "error", msg: "Não foi possível identificar a empresa para carregar os dados." });
    return;
  }

  // Abre o modal imediatamente (sem bloquear)
  setOpenOrCloseModalAnalisar(true);
  handleClose();

  setLoadingVersao(true);

  try {
    // Sempre busca TUDO da tela (verbas, impostos, relatório 20s, bases, resumo) para não ficar vazia
    await buscaDadosPrevia(idParaBusca);

    if (idVersaoSel !== 0) {
      // Para versões específicas, busca o snapshot da versão e sobrepõe no contexto (resumo, verbas da versão etc.)
      let versaoCompleta = null;
      if (projeto) {
        const response = await buscarVersaoCompletaProjeto(idVersaoSel);
        versaoCompleta = response?.versao || response?.data?.versao || response;
      } else {
        const response = await buscarVersaoCompletaPrevia(idVersaoSel);
        versaoCompleta = response?.versao || response?.data?.versao || response;
      }
      if (versaoCompleta) {
        const vc = sanitizeVersaoSnapshotArrays(versaoCompleta, cnpjSelecionado);
        setDados((prev) => {
          const forcarFolhaAoVivo = preferLiveFolhaOverSnapshot(
            prev.verbasComValores,
            vc.verbasComValores || [],
            cnpjSelecionado
          );

          const merged = { ...prev, ...vc };
          const arrayKeys = [
            "verbasEsocial",
            "verbasComValores",
            "relatorio20s",
            "verbasAjustadas",
            "impostos",
            "baseCalculo",
            "resumo",
            "resumoAtual",
            "contasResumo",
          ];
          arrayKeys.forEach((key) => {
            const fromVersao = vc[key];
            const fromPrev = prev[key];
            if (
              Array.isArray(fromPrev) &&
              fromPrev.length > 0 &&
              (!Array.isArray(fromVersao) || fromVersao.length === 0)
            ) {
              merged[key] = fromPrev;
            }
          });

          if (forcarFolhaAoVivo) {
            merged.verbasComValores = prev.verbasComValores;
            merged.verbasAjustadas = prev.verbasAjustadas;
            merged.verbasEsocial = prev.verbasEsocial;
            merged.impostos = prev.impostos;
            merged.baseCalculo = prev.baseCalculo;
            merged.relatorio20s = prev.relatorio20s;
            merged.contasResumo = prev.contasResumo;
            merged.resumo = prev.resumo;
            merged.resumoAtual = prev.resumoAtual;
          }

          return {
            ...merged,
            loading: false,
            loadingVersao: false,
            idVersao: idVersaoSel,
            tipoVersao: projeto ? "projeto" : "previa",
          };
        });
      } else {
        setDados((prev) => ({
          ...prev,
          loadingVersao: false,
          idVersao: idVersaoSel,
          tipoVersao: projeto ? "projeto" : "previa",
        }));
      }
    } else {
      // Versão Original: contexto já foi preenchido por buscaDadosPrevia; só seta idVersao e tipoVersao
      setDados((prev) => ({
        ...prev,
        loadingVersao: false,
        idVersao: 0,
        tipoVersao: projeto ? "projeto" : "previa",
      }));
    }

    // Última versão e flags de edição/tabs
    const lastVersaoId = versoes[versoes.length - 1] ? getIdVersao(versoes[versoes.length - 1]) : 0;
    const isLatest = idVersaoSel === lastVersaoId || idVersaoSel === 0;
    const statusCongelados = ["PREVIA-ENVIADA", "PREVIA-APROVADO", "PROJETO-INICIADO"];
    const statusAtual = data?.status || "";
    const isStatusCongelado = statusCongelados.includes(statusAtual);
    const isEditable = isLatest && !isStatusCongelado;
    const useTabs = isStatusCongelado;

    setDados((prev) => ({
      ...prev,
        cnpj: cnpjSelecionado || prev?.cnpj || null,
      isEditable,
      isTab: useTabs,
      loadingVersao: false,
    }));
  } catch (error) {
    console.error("Erro ao carregar dados da versão:", error);
    setObjSnackBar({ type: "error", msg: "Erro ao carregar dados da versão." });
    setLoadingVersao(false);
    setDados((prev) => ({ ...prev, loadingVersao: false }));
  }
};

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        {loading ? (
          <Loading loading={loading} />
        ) : (
          <DialogContent 
            sx={{ 
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div className={styles.containerTitle}>
              <div className={styles.title}>
                <FormatListNumberedIcon /> Versões
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setOpenObs(true)}
                >
                  Observações INSS
                </Button>
                {!gerarProjeto && (
                  <Button
                    variant="contained"
                    onClick={() => setModalNovaVersao(true)}
                    className={styles.btnAnalisar}
                    size="small"
                    disabled={['PREVIA-ENVIADA', 'PREVIA-APROVADO', 'PROJETO-INICIADO'].includes(data?.status)}
                  >
                    Adicionar Versão
                  </Button>
                )}
              </div>

            </div>
            <div className={styles.containerVersao}>
              {versoes.map((versao, index) => {
                const statusCongelados = ['PREVIA-ENVIADA', 'PREVIA-APROVADO', 'PROJETO-INICIADO'];
                const isCongelado = statusCongelados.includes(data?.status);
                const isUltimaVersao = index === versoes.length - 1;
                const isEditavel = isUltimaVersao && !isCongelado;
                const itemClasses = [
                  styles.itemVersao,
                  isEditavel && styles.itemVersaoEditavel,
                  !isEditavel && styles.itemVersaoCongelada,
                ].filter(Boolean).join(' ');
                
                return (
                  <div key={index} className={itemClasses}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isEditavel && <span className={styles.tagVersao}>Em edição</span>}
                      {!isEditavel && <span className={styles.tagVersao}>Congelada</span>}
                      <span>
                        <h4>{versao.descricao}</h4>
                        <p style={{ fontSize: ".8rem", color: "#A4A4A4" }}>
                          {versao.dtCadastro
                            ? new Date(versao.dtCadastro).toLocaleDateString(
                              "pt-BR"
                            )
                            : "-"}
                        </p>
                      </span>
                    </span>
                    {(index !== 0) && (!gerarProjeto ? (
                      <Button
                        variant="outlined"
                        onClick={() => handleAnalisar(versao)}
                        className={styles.btnFechar}
                        size="small"
                      >
                        {isCongelado ? 'Visualizar' : 'Analisar'}
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={() => handleGerarProjeto(versao)}
                        className={styles.btnFechar}
                        size="small"
                      >
                        Selecionar
                      </Button>
                    ))

                    }
                  </div>
                );
              })}
            </div>
            <div className={styles.containerBtn}>
              <Button
                variant="outlined"
                onClick={handleClose}
                className={styles.btnFechar}
              >
                Fechar
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>

      <Dialog
        open={modalNovaVersao}
        onClose={() => setModalNovaVersao(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          {loadingCreate ? (
            <Loading loading={loadingCreate} />
          ) : (
            <>
              <div className={styles.title}>
                <CopyAllIcon /> <h4>Copiar Versão</h4>
              </div>
              <br />
              <p>
                Seleciona abaixo a partir de qual versão você deseja iniciar e
                dê um nome para a nova:
              </p>
              <FormControl fullWidth sx={{ my: 2 }}>
                <InputLabel id="demo-simple-select-label">Versões</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={novaVersao.copiar}
                  label="Versões"
                  onChange={handleChangeCopia}
                >
                  {versoes.map((item) => {
                    return (
                      <MenuItem value={item} key={getIdVersao(item)}>
                        {item.descricao || `Versão ${getIdVersao(item)}`}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <TextField
                label="Nome da Versão"
                variant="outlined"
                fullWidth
                value={novaVersao.nome}
                InputProps={{ readOnly: true }}
              />
              <div className={styles.containerBtn}>
                <Button
                  variant="outlined"
                  onClick={() => setModalNovaVersao(false)}
                  className={styles.btnFechar}
                >
                  Fechar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreate}
                  className={
                    !novaVersao.nome || !novaVersao.copiar
                      ? ""
                      : styles.btnAnalisar
                  }
                  disabled={!novaVersao.nome || !novaVersao.copiar}
                >
                  Criar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <SnackBar
        objSnackBar={objSnackBar}
        setObjSnackBar={(obj) => setObjSnackBar(obj)}
      />

      <ModalObservacoesInss
        open={openObs}
        onClose={() => setOpenObs(false)}
        idPreviaOrCnpj={data?.idPreviaInss || data?.cnpj || id}
      />

      <ModalAnalisar
        open={openOrCloseModalAnalisar}
        setOpen={(value) => setOpenOrCloseModalAnalisar(value)}
        data={data}
        rechargeData={rechargeData}
        isTab={projeto}
      />

    </>
  );
};

export default ModalVersoes;
